/**
 * Shared categories and metadata
 * Used across frontend components and backend worker
 * Single source of truth for all category-related data
 */

export interface CategoryMetadata {
    value: string;
    label: string;
    shortLabel: string;
    emoji: string;
    color: {
        background: string;
        text: string;
    };
    description?: string;
}

/**
 * Complete category metadata for all product categories
 * Includes display labels, emojis, and color schemes
 */
export const CATEGORY_METADATA = {
    signature_mediterranean_chicken: {
        value: 'signature_mediterranean_chicken',
        label: 'Signature Mediterranean Chicken Range',
        shortLabel: 'Chicken Range',
        emoji: '⭐',
        color: {
            background: '#e8f5e9',
            text: '#2e7d32'
        },
        description: 'Balanced and naturally satisfying — made for those who want real flavour without compromising their health goals.\n\nLower in carbs and sugar, high in protein, and crafted with healthy fats from olive oil and homemade broth.'
    },
    cream_pasta: {
        value: 'cream_pasta',
        label: 'Cream Based Pastas',
        shortLabel: 'Cream Pasta',
        emoji: '🍝',
        color: {
            background: '#fff3cd',
            text: '#856404'
        }
    },
    tomato_pasta: {
        value: 'tomato_pasta',
        label: 'Tomato Based Pastas',
        shortLabel: 'Tomato Pasta',
        emoji: '🍝',
        color: {
            background: '#f8d7da',
            text: '#721c24'
        }
    },
    high_protein: {
        value: 'high_protein',
        label: 'High Protein',
        shortLabel: 'High Protein',
        emoji: '💪',
        color: {
            background: '#d1ecf1',
            text: '#0c5460'
        }
    },
    drink: {
        value: 'drink',
        label: 'Drinks',
        shortLabel: 'Drinks',
        emoji: '🥤',
        color: {
            background: '#cce5ff',
            text: '#004085'
        }
    },
    salad: {
        value: 'salad',
        label: 'Salads',
        shortLabel: 'Salads',
        emoji: '🥗',
        color: {
            background: '#d4edda',
            text: '#155724'
        }
    },
    dessert: {
        value: 'dessert',
        label: 'Desserts',
        shortLabel: 'Desserts',
        emoji: '🍰',
        color: {
            background: '#fbb6ce',
            text: '#d53f8c'
        }
    },
    pizza: {
        value: 'pizza',
        label: 'Pizza',
        shortLabel: 'Pizza',
        emoji: '🍕',
        color: {
            background: '#fed7e2',
            text: '#e53e3e'
        }
    },
    burger: {
        value: 'burger',
        label: 'Burgers',
        shortLabel: 'Burgers',
        emoji: '🍔',
        color: {
            background: '#feebc8',
            text: '#dd6b20'
        }
    },
    wraps: {
        value: 'wraps',
        label: 'Wraps',
        shortLabel: 'Wraps',
        emoji: '🌯',
        color: {
            background: '#fef5e7',
            text: '#b7791f'
        }
    },
    chips: {
        value: 'chips',
        label: 'Chips',
        shortLabel: 'Chips',
        emoji: '🍟',
        color: {
            background: '#ffe4b5',
            text: '#d2691e'
        }
    },
    pasta: {
        value: 'pasta',
        label: 'Pasta',
        shortLabel: 'Pasta',
        emoji: '🍝',
        color: {
            background: '#c6f6d5',
            text: '#38a169'
        }
    },
    appetizer: {
        value: 'appetizer',
        label: 'Appetizers',
        shortLabel: 'Appetizers',
        emoji: '🥗',
        color: {
            background: '#bee3f8',
            text: '#3182ce'
        }
    },
    seafood: {
        value: 'seafood',
        label: 'Seafood',
        shortLabel: 'Seafood',
        emoji: '🐟',
        color: {
            background: '#e9d8fd',
            text: '#805ad5'
        }
    }
} as const;

/**
 * ProductCategory type derived from CATEGORY_METADATA keys
 * Single source of truth - add new categories to CATEGORY_METADATA above
 */
export type ProductCategory = keyof typeof CATEGORY_METADATA;

/**
 * Array of all categories in display order
 */
export const CATEGORIES: CategoryMetadata[] = [
    CATEGORY_METADATA.burger,
    CATEGORY_METADATA.wraps,
    CATEGORY_METADATA.chips,
    CATEGORY_METADATA.signature_mediterranean_chicken,
    CATEGORY_METADATA.cream_pasta,
    CATEGORY_METADATA.tomato_pasta,
    CATEGORY_METADATA.high_protein,
    CATEGORY_METADATA.drink,
    CATEGORY_METADATA.salad,
    CATEGORY_METADATA.dessert,
    CATEGORY_METADATA.pizza,
    CATEGORY_METADATA.pasta,
    CATEGORY_METADATA.appetizer,
    CATEGORY_METADATA.seafood,
];

/**
 * Get metadata for a specific category
 */
export function getCategoryMetadata(category: ProductCategory): CategoryMetadata {
    return CATEGORY_METADATA[category];
}

/**
 * Get display label for a category
 */
export function getCategoryLabel(category: ProductCategory): string {
    return CATEGORY_METADATA[category].label;
}

/**
 * Get emoji for a category
 */
export function getCategoryEmoji(category: ProductCategory): string {
    return CATEGORY_METADATA[category].emoji;
}

/**
 * Get color scheme for a category
 */
export function getCategoryColor(category: ProductCategory): { background: string; text: string } {
    return CATEGORY_METADATA[category].color;
}
